import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { AvisConstants } from "../../data/avisConstants";
import { PortalAuthenticationResult } from "../../generated/data-contracts";
import { IAxiosError, api } from "../../utils";
import { isMobileView } from "../shared/appStates/appStates";
import { UnauthorizedLayout } from "../shared/components";
import { AvisPrimaryButton } from "../shared/components/buttons";
import { AvisConfirmModalShort } from "../shared/components/modal";
import { AvisContext } from "../shared/context/AvisContext";
import { ErrorLoadingDataHoc } from "../shared/error/ErrorLoadingDataHoc";

interface IAcceptTermsPageProps {
  onUpdate: (data?: PortalAuthenticationResult) => void;
}

export function AcceptTermsPage(props: Readonly<IAcceptTermsPageProps>) {
  const { onUpdate } = props;
  const termsOfUseKey = "portal_TermsOfUse";
  const [error, setError] = useState<IAxiosError>();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [bottom, setBottom] = useState<boolean>(false);
  const { ctxUser, getText, updateUser, logout, res, ctxPortalSettings } =
    useContext(AvisContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const terms = getText(termsOfUseKey);

  useEffect(() => {
    if (terms && isMobileView.value) {
      setBottom(true);
    }
  }, [terms]);

  const isCloseToBottom = (e: any) =>
    e.target.scrollHeight - e.target.scrollTop - 20 <= e.target.clientHeight;

  const checkSecurityClaims = () => {
    api
      .securityClaims()
      .then((res) => {
        updateUser(res.data);
        onUpdate(res.data);
      })
      .catch(setError);
  };

  const handleAcceptClick = () => {
    setError(undefined);

    if (!ctxUser) {
      onUpdate();

      return;
    }

    setSubmitting(true);

    api
      .securityAcceptUseTerms()
      .then(checkSecurityClaims)
      .catch((error) => {
        setError(error);
        setSubmitting(false);
      });
  };

  const acceptActions = (
    <div className="accept-terms-actions">
      <AvisPrimaryButton
        disabled={submitting || isModalOpen}
        onClick={logout}
        label={res("button_Close")}
      />
      <AvisPrimaryButton
        aria-disabled={!bottom}
        disabled={!bottom || submitting || isModalOpen}
        onClick={() => setIsModalOpen(true)}
        label={res("button_Confirm")}
      />
    </div>
  );
  const renderTermsAndConditionLastUpdateDate = () => {
    if (!ctxPortalSettings) return;

    const termsDateMoment = moment(
      ctxPortalSettings.portalTermsAndConditionsDate,
    );

    return (
      <div className="accept-terms-update-date">
        {res(
          "user_AcceptTerms_label_Date",
          termsDateMoment.format(AvisConstants.date_format),
        )}
      </div>
    );
  };

  const renderContent = (
    <ErrorLoadingDataHoc error={error}>
      {terms && (
        <div
          className="accept-terms-content"
          onScroll={(e: React.UIEvent<HTMLDivElement, UIEvent>) => {
            if (isCloseToBottom(e)) setBottom(true);
          }}
        >
          <span>{terms}</span>
        </div>
      )}
    </ErrorLoadingDataHoc>
  );

  return (
    <>
      <UnauthorizedLayout
        className="accept-terms"
        info={acceptActions}
        textToDisplayKey="portal_TermsOfUse_1"
      >
        <h2 className="accept-terms-title">{res("user_AcceptTerms_Title")}</h2>
        {renderTermsAndConditionLastUpdateDate()}
        {renderContent}
      </UnauthorizedLayout>
      <AvisConfirmModalShort
        isOpen={isModalOpen}
        content={res("user_AcceptTerms_ConfirmAccept")}
        confirm={handleAcceptClick}
        cancel={logout}
        title={res("title_Confirm")}
        disableControls={submitting}
      />
    </>
  );
}
